import {
  browseCollections,
  browseLessons,
  getGeneration,
  listGenerations,
  listLessonReviews,
  reviewLesson,
} from '@platform/client';
import { lessonReviewsSchema } from '@platform/schema/generation';
import { lessonWorkspaceSchema } from '@platform/schema/review';

import { lessonPlace, placeQuery } from './places';

import type { ModulePlace, Place, Places } from './places';
import type { CollectionKind } from '@platform/schema/content';

/**
 * The lists a filter is picked from, rather than typed into.
 *
 * A topic or a status is an enum the client already holds, so `FilterSelect` gets those
 * options from the schema. A course, a module and a job are rows, and have to be fetched
 * before the filter can be offered.
 *
 * Off the `library` prefix on purpose: the membership writes invalidate that prefix, and
 * the set of things to pick from does not change when a path gains a module.
 */

/**
 * Enough to pick from. Past it the fix is searching, not a bigger number: `PlacePicker`
 * sends what was typed as `q`, so a module beyond the first hundred is still found.
 */
const OPTIONS = 100;

/**
 * Every collection of one kind, to pick one from — asked the same way by every screen
 * that asks.
 *
 * One function over a kind rather than one per kind, for the reason `Library` itself
 * exists: the cap, the `staleTime` and the key convention were written out twice and had
 * already started to disagree about what the constant was called.
 *
 * The kind is in the key, so two screens picking modules share one cache and cannot be
 * showing different sets at once.
 */
export const pickerQuery = (
  kind: CollectionKind,
  q = '',
  parentId?: string,
) => ({
  queryKey: [kind, 'sources', q, ...(parentId === undefined ? [] : [parentId])],
  queryFn: () =>
    browseCollections({
      query: {
        kind,
        limit: OPTIONS,
        ...(q === '' ? {} : { q }),
        ...(parentId === undefined ? {} : { parentId }),
      },
      throwOnError: true,
    }),
  /**
   * These lists are close to static, and the app's 10s default would refetch on every
   * navigation back to a screen carrying a picker.
   */
  staleTime: 5 * 60 * 1000,
});

/**
 * Every run, to filter by the one that made a module — GEN-60.
 *
 * The same key `GenerationJobs` uses, and that screen spreads this rather than declaring
 * its own: two keys for one list is two caches that can be showing different sets of jobs
 * at once. The five-second poll stays on that screen, because a `refetchInterval` belongs
 * to the observer that wants it and a filter dropdown does not.
 */
export const jobsQuery = () => ({
  queryKey: ['generations'],
  queryFn: () => listGenerations({ throwOnError: true }),
  staleTime: 5 * 60 * 1000,
});

/**
 * One job's detail — CURR-29. Every screen that follows a job reads this key, so it holds
 * one shape: the job page cached the response and Curriculum the detail, and whichever
 * opened second read the other's and threw.
 */
export const jobQuery = (id: string) => ({
  queryKey: ['generation', id],
  queryFn: async () =>
    (await getGeneration({ path: { id }, throwOnError: true })).data,
});

/**
 * One lesson as the workspace reads it — KISS-15. Parsed here, once, for the reason
 * `jobQuery` unwraps: three screens read this key, and two cached the response while the
 * onboarding preview cached the parsed lesson, so whichever opened first emptied the
 * others. Parsed rather than unwrapped because the SDK types a scene's `kind` as a string.
 */
export const reviewQuery = (id: string) => ({
  queryKey: ['review', id],
  queryFn: async () =>
    lessonWorkspaceSchema.parse(
      (await reviewLesson({ path: { id }, throwOnError: true })).data,
    ),
});

/** A lesson's reviews, newest first — CURR-55. Parsed, as `reviewQuery` is, for the brands. */
export const lessonReviewsQuery = (id: string) => ({
  queryKey: ['review', id, 'reviews'],
  queryFn: async () =>
    lessonReviewsSchema.parse(
      (await listLessonReviews({ path: { id }, throwOnError: true })).data,
    ),
});

/**
 * What the Course, Module and Lesson filters offer — ADMIN-18. A module is offered from
 * the picked course, and a lesson from the picked course and module.
 */
export const courseSearch = (q: string) => ({
  ...pickerQuery('COURSE', q),
  select: (found: Collections): Place[] =>
    found.data.collections.map(({ id, title }) => ({ id, title })),
});

export const moduleSearch = (course: Place | null) => (q: string) => ({
  ...pickerQuery('MODULE', q, course?.id),
  select: (found: Collections): ModulePlace[] =>
    found.data.collections.map(({ id, title, parentId, parentTitle }) => ({
      id,
      title,
      ...(parentId === undefined || parentTitle === undefined
        ? {}
        : { course: { id: parentId, title: parentTitle } }),
    })),
});

/** Under the `lessons` prefix, so an archive refreshes it with the table. */
export const lessonSearch =
  ({ course, module }: Places) =>
  (q: string) => ({
    queryKey: ['lessons', 'places', q, course?.id, module?.id],
    queryFn: () =>
      browseLessons({
        query: {
          limit: OPTIONS,
          sort: 'TITLE_ASC',
          ...(q === '' ? {} : { q }),
          ...placeQuery({ course, module, lesson: null }),
        },
        throwOnError: true,
      }),
    select: (found: Awaited<ReturnType<typeof browseLessons<true>>>) =>
      found.data.lessons.map(lessonPlace),
    staleTime: 5 * 60 * 1000,
  });

type Collections = Awaited<ReturnType<typeof browseCollections<true>>>;
